/**
 * Mailchimp import helpers — pure, DOM-free logic shared by the client cleaner
 * (src/lib/mailchimpClean.ts) and the server fetch callable
 * (functions/src/importMailchimp.ts).
 *
 * Guests are composed in Mailchimp, then imported here by their "View this
 * email in your browser" campaign URL. This module holds the parts that must
 * work without a DOM: URL allowlisting (an SSRF guard on the server), tracking
 * link decoding, and subject extraction.
 */

/**
 * Hosts a Mailchimp campaign archive is served from. The server only fetches
 * URLs whose host matches one of these, which keeps the fetch callable from
 * being turned into a general-purpose request proxy (SSRF).
 */
const ALLOWED_ARCHIVE_HOSTS = [
  'campaign-archive.com', // us1.campaign-archive.com, us20.campaign-archive.com, …
  'list-manage.com', // <user>.<dc>.list-manage.com (view-in-browser redirect)
  'mailchi.mp', // mailchi.mp/<hash>/<slug> short archive links
];

/**
 * Validate a user-supplied Mailchimp archive URL and return a parsed URL, or
 * null if it isn't an https link to an allowlisted Mailchimp archive host.
 */
export function parseMailchimpArchiveUrl(raw: string): URL | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase();
  const allowed = ALLOWED_ARCHIVE_HOSTS.some(
    (base) => host === base || host.endsWith(`.${base}`),
  );
  return allowed ? url : null;
}

/** True when `raw` is a fetchable Mailchimp archive URL. */
export function isMailchimpArchiveUrl(raw: string): boolean {
  return parseMailchimpArchiveUrl(raw) !== null;
}

/**
 * Decode a Mailchimp click-tracking href to its real destination, or null if
 * `href` isn't a decodable tracking link. The `p` query param is
 * base64 → JSON `{ p: "<json string>" }` → the inner JSON's `url` (with
 * escaped `\/` slashes).
 */
export function unwrapMailchimpTrackingUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase();
  const isTrackingHost =
    host === 'click.mailchimp.com' ||
    host.endsWith('.list-manage.com') ||
    host.endsWith('.mailchimp.com');
  if (!isTrackingHost || !url.pathname.includes('/track/click')) return null;

  const p = url.searchParams.get('p');
  if (!p) return null;

  try {
    const normalized = p.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = decodeBase64(padded);
    const outer = JSON.parse(decoded) as { p?: unknown };
    const inner = (typeof outer.p === 'string' ? JSON.parse(outer.p) : outer.p) as {
      url?: unknown;
    };
    if (inner && typeof inner.url === 'string') {
      return inner.url.replace(/\\\//g, '/');
    }
  } catch {
    // Undecodable payload — caller keeps the original href.
  }
  return null;
}

/** Base64 decode that works in both the browser (atob) and Node (Buffer). */
function decodeBase64(b64: string): string {
  if (typeof atob === 'function') return atob(b64);
  // Node fallback (functions runtime): interpret bytes as latin1 like atob.
  return Buffer.from(b64, 'base64').toString('binary');
}

/**
 * Best-effort subject line from a fetched archive document: the campaign
 * subject is Mailchimp's `<title>`, falling back to the og:title meta tag.
 * Returns null when neither is present.
 */
export function extractSubjectFromArchive(html: string): string | null {
  const title = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1];
  const cleanedTitle = title ? decodeHtmlEntities(title).trim() : '';
  if (cleanedTitle) return cleanedTitle;

  const og = html.match(
    /<meta[^>]+property=["']og:title["'][^>]*content=["']([^"']*)["']/i,
  )?.[1];
  const cleanedOg = og ? decodeHtmlEntities(og).trim() : '';
  return cleanedOg || null;
}

/** Decode the handful of named/numeric entities that show up in a title. */
function decodeHtmlEntities(input: string): string {
  return input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(parseInt(code, 16)));
}
